import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import {useWardrobe} from "../context/WardrobeContext";
import {money} from "../utils/money";

interface CategoryCount {
    name: string;
    count: number;
}

// Числа приходят из единого снимка сигналов (wardrobe-signals): «ни разу не носили» — то же
// множество, что в аналитике. Средняя ценность — ItemScore, а не legacy-рейтинг 1500.
interface StatsSummary {
    activeItems: number;
    inTrashBin: number;
    thrownAway: number;
    totalValueRub: number | null;
    /** Средний ItemScore в нативной шкале 1200..1800. */
    itemScore: number | null;
    /** Тот же ItemScore в шкале 0–100. */
    itemScore100: number | null;
    totalWears: number;
    neverWornCount: number;
    notWornRecentlyCount?: number;
    notWornDaysThreshold?: number;
    topCategories: CategoryCount[];
}

// QA-15: в подсказке нет внутренних терминов («ItemScore», шкала 1200..1800) — только смысл метрики.
const ITEM_SCORE_HINT = 'Средняя полезность вещей по шкале от 0 до 100: 50 — нейтральная середина, '
    + 'выше — вещи полезнее для гардероба. Считается по вещам, у которых есть оценка.';

// F09: интегральная оценка называется «Полезность для вашего гардероба»; «Состояние» —
// только физическое состояние вещи и в этот ряд не входит.
const VALUE_SCORE_LABEL = 'Полезность для гардероба';

// F09: «Ни разу не носили» — утверждение о жизни вещи; журнал знает только свои записи.
const NO_WEAR_RECORDS_LABEL = 'Нет записей о носке';
const NO_WEAR_RECORDS_HINT = 'Вещи, по которым в журнале нет ни одной отметки носки. '
    + 'Это не значит, что их не носили — значит, что носку не отмечали.';

const Kpi: React.FC<{ label: string; value: string; hint?: string; suffix?: string; valueTitle?: string }> =
    ({label, value, hint, suffix, valueTitle}) => (
    <div className="card p-4" title={hint}>
        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100" title={valueTitle}>
            {value}{suffix && value !== '—' && <span className="text-sm font-normal text-gray-400 ml-0.5">{suffix}</span>}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
            {label}
            {hint && (
                <span className="cursor-help text-xs text-gray-400" aria-label={hint} role="img">ⓘ</span>
            )}
        </div>
    </div>
);

const WardrobeStats: React.FC = () => {
    const {selectedOwnerId} = useWardrobe();
    const [data, setData] = useState<StatsSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            const res = await api.get<StatsSummary>('/stats/summary', {params});
            setData(res.data);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [selectedOwnerId]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return <div className="text-gray-500 dark:text-gray-400" data-testid="stats-loading">Загрузка статистики…</div>;
    }

    if (error) {
        return (
            <div className="card p-4" data-testid="stats-error">
                <span className="text-gray-600 dark:text-gray-300 mr-3">Не удалось загрузить статистику</span>
                <button onClick={load} className="text-brand-600 hover:underline">Повторить</button>
            </div>
        );
    }

    const s = data!;
    const score = s.itemScore100 != null ? String(s.itemScore100) : '—';
    // F12: разряды и компактный вид от миллиона; точная сумма — в title.
    const value = money(s.totalValueRub);

    return (
        <div data-testid="wardrobe-stats" className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi label="Активные вещи" value={String(s.activeItems)}/>
            <Kpi label="Стоимость" value={value.text} valueTitle={value.title}/>
            <Kpi label={VALUE_SCORE_LABEL} value={score} suffix="/100" hint={ITEM_SCORE_HINT}/>
            <Kpi label={NO_WEAR_RECORDS_LABEL} value={String(s.neverWornCount)} hint={NO_WEAR_RECORDS_HINT}/>
        </div>
    );
};

export default WardrobeStats;
